import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search } from 'lucide-react';
import { Reveal, WordReveal } from '../components/Motion';
import { uxProjects, UX_CATEGORIES, websiteProjects } from '../mock/data';

const Hero = ({ tag, title, subtitle }) => (
  <section className="px-6 md:px-12 lg:px-20 pt-32 pb-16 relative overflow-hidden">
    <div className="blob" style={{ width: 500, height: 500, background: 'var(--accent)', top: '-20%', right: '-10%', opacity: 0.12 }}/>
    <div className="max-w-[1500px] mx-auto relative">
      <span className="section-eyebrow">{tag}</span>
      <h1 className="h-display mt-8 text-[12vw] md:text-[8.5vw]"><WordReveal text={title.replace(/^./, c => c.toUpperCase())}/></h1>
      <p className="mt-8 max-w-2xl text-muted text-[15px] leading-relaxed">{subtitle}</p>
    </div>
  </section>
);

export default function UXFolio() {
  const [active, setActive] = React.useState('All');
  const [q, setQ] = React.useState('');
  const filtered = uxProjects.filter(p => (active === 'All' || p.cat === active) && p.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Hero
        tag="UX folio — case studies"
        title="the archive."
        subtitle="Twelve selected projects across enterprise, healthcare, food delivery, transit and wellness. Each piece reflects a blend of strategy, user empathy and aesthetically functional design."
      />
      <section className="px-6 md:px-12 lg:px-20 pb-32">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-6 py-6 border-y" style={{ borderColor: 'var(--line)' }}>
            <div className="flex gap-2 flex-wrap">
              {UX_CATEGORIES.map(c => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-[12px] uppercase tracking-widest transition-colors ${active === c ? 'bg-accent text-[color:var(--bg)]' : 'border'}`}
                  style={active !== c ? { borderColor: 'var(--line-strong)' } : {}}
                >{c}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{ borderColor: 'var(--line-strong)' }}>
              <Search size={14} className="text-dim"/>
              <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search" className="bg-transparent outline-none text-[13px] w-40"/>
              <span className="kbd">⌘K</span>
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-y-16 gap-x-8">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.04}>
                <Link to={`/work/${p.slug}`} className="group block" data-cursor="hover">
                  <div className="relative aspect-[5/4] overflow-hidden rounded-2xl">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]"/>
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.55))' }}/>
                  </div>
                  <div className="mt-5 flex items-start justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-accent">{p.cat} • {p.year}</div>
                      <h3 className="h-display text-2xl md:text-3xl mt-2">{p.title}</h3>
                      <p className="text-muted text-[13px] mt-2 max-w-md">{p.desc}</p>
                    </div>
                    <ArrowUpRight size={18} className="text-dim group-hover:text-accent transition-colors flex-shrink-0"/>
                  </div>
                </Link>
              </Reveal>
            ))}
            {filtered.length === 0 && <p className="text-muted">No projects match. Try a different filter.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}

export const WebsiteFolio = () => (
  <div>
    <Hero
      tag="Website portfolio"
      title="sites that ship."
      subtitle="A selection of 80+ websites delivered over the years — e-learning, manufacturing, music, healthcare, travel. Each built to be visually compelling, user-centric and performant."
    />
    <section className="px-6 md:px-12 lg:px-20 pb-32">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-2 gap-10">
        {websiteProjects.map((p,i)=>(
          <Reveal key={p.id} delay={i*0.04}>
            <Link
              to={`/website/${p.slug}`}
              data-testid={`website-card-${p.slug}`}
              className="card-prem overflow-hidden group block"
              data-cursor="hover"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]"/>
              </div>
              <div className="p-7 flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-accent">{p.cat}</div>
                  <h3 className="h-display text-2xl mt-2">{p.title}</h3>
                  <p className="text-muted text-[13px] mt-2">{p.desc}</p>
                  <span className="link-arrow mt-5 inline-flex">
                    View project <span className="arrow"><ArrowUpRight size={14}/></span>
                  </span>
                </div>
                <ArrowUpRight size={20} className="text-dim group-hover:text-accent transition-colors flex-shrink-0"/>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  </div>
);

const ILLUSTRATIONS = [
  { id: 'i1', url: '/illu/g1.png' },
  { id: 'i2', url: '/illu/g2.png' },
  { id: 'i3', url: '/illu/g3.png' },
  { id: 'i4', url: '/illu/g4.png' },
  { id: 'i5', url: '/illu/g5.png' },
  { id: 'i6', url: '/illu/g6.png' },
];

export const Illustration = () => (
  <div>
    <Hero tag="Illustration" title="side studio." subtitle="Hand-crafted and generative illustrations — texture, color, and a bit of restraint."/>
    <section className="px-6 md:px-12 lg:px-20 pb-32">
      <div className="max-w-[1500px] mx-auto columns-2 md:columns-3 gap-6 space-y-6">
        {ILLUSTRATIONS.map(item => (
          <div key={item.id} className="rounded-2xl overflow-hidden break-inside-avoid" data-cursor="hover">
            <img src={item.url} alt="" className="w-full hover:scale-105 transition-transform duration-[1200ms]"/>
          </div>
        ))}
      </div>
    </section>
  </div>
);

const AI_ART = [
  { id: 'aa1', url: '/ai/art1.png' },
  { id: 'aa2', url: '/ai/art2.png' },
  { id: 'aa3', url: '/ai/art3.png' },
  { id: 'aa4', url: '/ai/art4.png' },
  { id: 'aa5', url: '/ai/art5.png' },
  { id: 'aa6', url: '/ai/art6.png' },
  { id: 'aa7', url: '/ai/art7.png' },
  { id: 'aa8', url: '/ai/art8.png' },
  { id: 'aa9', url: '/ai/art9.png' },
  { id: 'aa10', url: '/ai/art10.png' },
  { id: 'aa11', url: '/ai/art11.png' },
  { id: 'aa12', url: '/ai/art12.png' },
  { id: 'aa13', url: '/ai/art13.png' },
  { id: 'aa14', url: '/ai/art14.png' },
  { id: 'aa15', url: '/ai/art15.png' },
  { id: 'aa16', url: '/ai/art16.png' },
  { id: 'aa17', url: '/ai/art17.png' },
  { id: 'aa18', url: '/ai/art18.png' },
];

export const AIArt = () => (
  <div>
    <Hero tag="Generative art" title="latent gallery." subtitle="A small collection of generative pieces. Prompt, iterate, curate, post."/>
    <section className="px-6 md:px-12 lg:px-20 pb-32">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-3 gap-6">
        {AI_ART.map((item) => (
          <div key={item.id} className="aspect-square rounded-2xl overflow-hidden" data-cursor="hover">
            <img src={item.url} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-[1400ms]"/>
          </div>
        ))}
      </div>
    </section>
  </div>
);